import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { BadgeCheck, Upload, Linkedin, FileText, Send, CheckCircle, Clock, XCircle, ExternalLink } from 'lucide-react';

export default function Verification() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    documents: [],
    linkedin_verified: false,
    notes: ''
  });
  
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.get('/verification/status');
        setStatus(response.data);
      } catch (error) {
        console.error('Error fetching verification status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);
  
  const uploadDocument = async (file) => {
    setUploading(true);
    try {
      const sigResponse = await api.get('/cloudinary/signature?folder=profiles/verification&resource_type=raw');
      const sig = sigResponse.data;
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      const uploadUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/raw/upload`;
      const uploadResponse = await fetch(uploadUrl, { method: 'POST', body: formData });
      const result = await uploadResponse.json();
      
      setForm(prev => ({
        ...prev,
        documents: [...prev.documents, result.secure_url]
      }));
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };
  
  const removeDocument = (index) => {
    setForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.documents.length === 0 && !form.linkedin_verified) {
      alert('Please upload at least one document or verify your LinkedIn profile');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/verification/request', form);
      const response = await api.get('/verification/status');
      setStatus(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit verification request');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout title="Get Verified">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="Get Verified">
      <div className="max-w-2xl">
        {/* Status Banner */}
        {status?.is_verified ? (
          <div className="bg-green-50 border border-green-200 p-6 mb-6 flex items-center gap-4">
            <BadgeCheck className="text-green-500" size={48} />
            <div>
              <h2 className="text-lg font-semibold text-green-800">You're Verified!</h2>
              <p className="text-green-700">Your profile displays the verified badge, building trust with clients.</p>
            </div>
          </div>
        ) : status?.latest_request?.status === 'pending' ? (
          <div className="bg-yellow-50 border border-yellow-200 p-6 mb-6 flex items-center gap-4">
            <Clock className="text-yellow-500" size={48} />
            <div>
              <h2 className="text-lg font-semibold text-yellow-800">Verification Pending</h2>
              <p className="text-yellow-700">Your request is being reviewed. We'll notify you once it's processed.</p>
            </div>
          </div>
        ) : status?.latest_request?.status === 'rejected' ? (
          <div className="bg-red-50 border border-red-200 p-6 mb-6">
            <div className="flex items-center gap-4 mb-3">
              <XCircle className="text-red-500" size={48} />
              <div>
                <h2 className="text-lg font-semibold text-red-800">Verification Not Approved</h2>
                <p className="text-red-700">Your previous request was not approved. You can submit a new request.</p>
              </div>
            </div>
            {status.latest_request.admin_notes && (
              <p className="text-sm text-red-600 bg-red-100 p-3 mt-2">
                <strong>Reason:</strong> {status.latest_request.admin_notes}
              </p>
            )}
          </div>
        ) : null}
        
        {/* Benefits */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            <BadgeCheck className="inline mr-2 text-blue-500" size={24} />
            Why Get Verified?
          </h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-0.5" size={18} />
              <span className="text-neutral-700">Verified badge on your profile builds trust with clients</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-0.5" size={18} />
              <span className="text-neutral-700">Stand out in search results and browse pages</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-0.5" size={18} />
              <span className="text-neutral-700">Higher chances of getting shortlisted for briefs</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-0.5" size={18} />
              <span className="text-neutral-700">Demonstrates commitment to professionalism</span>
            </li>
          </ul>
        </div>
        
        {/* Verification Form */}
        {!status?.is_verified && status?.latest_request?.status !== 'pending' && (
          <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 p-6">
            <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
              Submit Verification Request
            </h3>
            
            {/* Documents Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <FileText size={14} className="inline mr-1" />
                Upload Supporting Documents
              </label>
              <p className="text-sm text-neutral-500 mb-3">
                Upload certifications, ID proof, or professional credentials (PDF or images)
              </p>
              
              <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-neutral-200 cursor-pointer hover:border-[#FF6B35] transition-colors mb-3">
                <Upload size={20} className="text-neutral-400" />
                <span className="text-neutral-600">{uploading ? 'Uploading...' : 'Choose file'}</span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => e.target.files?.[0] && uploadDocument(e.target.files[0])}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
              
              {form.documents.length > 0 && (
                <ul className="space-y-2">
                  {form.documents.map((doc, i) => (
                    <li key={i} className="flex items-center justify-between bg-neutral-50 px-3 py-2">
                      <a href={doc} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline flex items-center gap-1 text-sm truncate">
                        <ExternalLink size={14} />
                        Document {i + 1}
                      </a>
                      <button type="button" onClick={() => removeDocument(i)} className="text-neutral-400 hover:text-red-500">
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            {/* LinkedIn Verification */}
            <div className="mb-6">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.linkedin_verified}
                  onChange={(e) => setForm(prev => ({ ...prev, linkedin_verified: e.target.checked }))}
                  className="w-5 h-5 accent-[#FF6B35]"
                />
                <span className="text-neutral-700">
                  <Linkedin size={16} className="inline mr-1 text-blue-600" />
                  I confirm my LinkedIn profile is accurate and up-to-date
                </span>
              </label>
              <p className="text-sm text-neutral-500 ml-8 mt-1">
                Make sure your LinkedIn URL is added in your profile
              </p>
            </div>
            
            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Additional Notes (Optional)</label>
              <textarea
                placeholder="Any additional information to support your verification..."
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none resize-none"
              />
            </div>
            
            <button
              type="submit"
              disabled={submitting}
              className="btn-researcher px-8 py-3 flex items-center gap-2"
            >
              {submitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Send size={18} />
                  Submit for Verification
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
